import React, { useState, useEffect, useCallback } from "react";
import axios from "axios";
import { Wallet, Plus, Trash2, ArrowDownCircle, ArrowUpCircle, FileText } from "lucide-react";
import { rank } from "../../lib/rbac";

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;
const CATEGORIES = ["Float", "Housekeeping", "Stationary", "Maintenance", "Travel", "Food", "Misc"];

export default function PettyCash() {
  const [data, setData] = useState({ entries: [], balance: 0, total_in: 0, total_out: 0 });
  const [user, setUser] = useState(null);
  const [uploading, setUploading] = useState(false);
  const [form, setForm] = useState({ description: "", category: "Misc", direction: "OUT", amount: 0, receipt_file: "", date: new Date().toISOString().split("T")[0] });

  const fetchAll = useCallback(async () => {
    try {
      const [r, me] = await Promise.all([
        axios.get(`${BACKEND_URL}/api/petty-cash`),
        axios.get(`${BACKEND_URL}/api/auth/me`),
      ]);
      setData(r.data); setUser(me.data.user);
    } catch (e) { console.error(e); }
  }, []);
  useEffect(() => { fetchAll(); }, [fetchAll]);

  const canDelete = user && rank(user.role) >= 60;

  const uploadReceipt = async (e) => {
    const file = e.target.files?.[0]; if (!file) return;
    setUploading(true);
    try { const fd = new FormData(); fd.append("file", file); const res = await axios.post(`${BACKEND_URL}/api/upload`, fd, { headers: { "Content-Type": "multipart/form-data" } }); setForm(f => ({ ...f, receipt_file: res.data.path })); }
    catch (err) { alert("Upload failed"); }
    setUploading(false);
  };

  const addEntry = async (e) => {
    e.preventDefault();
    try { await axios.post(`${BACKEND_URL}/api/petty-cash`, { ...form, amount: parseFloat(form.amount) }); setForm({ ...form, description: "", amount: 0, receipt_file: "" }); fetchAll(); }
    catch (err) { alert(err.response?.data?.detail || "Not allowed"); }
  };
  const delEntry = async (id) => { try { await axios.delete(`${BACKEND_URL}/api/petty-cash/${id}`); fetchAll(); } catch (e) { alert(e.response?.data?.detail || "Not allowed"); } };

  const inp = "w-full bg-card_bg border border-border_soft rounded-xl p-3 text-text_primary text-sm";

  return (
    <div className="space-y-8 animate-in fade-in slide-in-from-bottom-8 duration-700" data-testid="petty-cash-page">
      <div>
        <h1 className="text-4xl font-serif text-text_primary mb-3">Petty Cash</h1>
        <p className="text-text_secondary text-sm">Record cash top-ups and expenses with receipts for your branch.</p>
      </div>

      <div className="grid grid-cols-1 md:grid-cols-3 gap-4">
        <div className="bg-card_bg border border-border_soft rounded-2xl p-6"><p className="text-[11px] uppercase tracking-wider text-text_secondary mb-2">Cash In Hand</p><p className="text-3xl font-bold text-gold" data-testid="petty-balance">₹{data.balance.toLocaleString()}</p></div>
        <div className="bg-card_bg border border-border_soft rounded-2xl p-6"><p className="text-[11px] uppercase tracking-wider text-text_secondary mb-2">Total Topped Up</p><p className="text-3xl font-bold text-accent_success">₹{data.total_in.toLocaleString()}</p></div>
        <div className="bg-card_bg border border-border_soft rounded-2xl p-6"><p className="text-[11px] uppercase tracking-wider text-text_secondary mb-2">Total Spent</p><p className="text-3xl font-bold text-accent_danger">₹{data.total_out.toLocaleString()}</p></div>
      </div>

      <div className="grid grid-cols-1 lg:grid-cols-3 gap-6">
        <form onSubmit={addEntry} className="bg-card_bg border border-border_soft rounded-2xl p-6 space-y-4 lg:col-span-1 h-fit">
          <h2 className="text-lg font-bold text-text_primary flex items-center gap-2"><Wallet size={18} className="text-gold" /> New Entry</h2>
          <div className="flex gap-2">
            <button type="button" onClick={() => setForm({ ...form, direction: "IN" })} className={`flex-1 py-2.5 rounded-xl text-sm font-semibold flex items-center justify-center gap-1 ${form.direction === "IN" ? "bg-accent_success/20 border border-accent_success text-accent_success" : "bg-main_bg border border-border_soft text-text_secondary"}`}><ArrowDownCircle size={15} /> Money In</button>
            <button type="button" onClick={() => setForm({ ...form, direction: "OUT" })} className={`flex-1 py-2.5 rounded-xl text-sm font-semibold flex items-center justify-center gap-1 ${form.direction === "OUT" ? "bg-primary/20 border border-primary text-gold" : "bg-main_bg border border-border_soft text-text_secondary"}`}><ArrowUpCircle size={15} /> Money Out</button>
          </div>
          <input required data-testid="petty-description" placeholder="Description" value={form.description} onChange={e => setForm({ ...form, description: e.target.value })} className={inp} />
          <select data-testid="petty-category" value={form.category} onChange={e => setForm({ ...form, category: e.target.value })} className={inp}>{CATEGORIES.map(c => <option key={c}>{c}</option>)}</select>
          <input required type="number" min="0" step="any" placeholder="Amount (₹)" value={form.amount} onChange={e => setForm({ ...form, amount: e.target.value })} className={inp} data-testid="petty-amount" />
          <input type="date" value={form.date} onChange={e => setForm({ ...form, date: e.target.value })} className={inp} />
          <div>
            <label className="text-xs text-text_secondary mb-1 block">Receipt / Invoice (optional)</label>
            <input type="file" accept="image/*,application/pdf" onChange={uploadReceipt} data-testid="petty-receipt-upload" className="w-full text-xs text-text_secondary file:mr-3 file:py-2 file:px-3 file:rounded-lg file:border-0 file:bg-primary file:text-[#111827] file:font-bold file:cursor-pointer" />
            {uploading && <p className="text-xs text-gold mt-1">Uploading...</p>}
            {form.receipt_file && !uploading && <p className="text-xs text-accent_success mt-1">✓ Receipt attached</p>}
          </div>
          <button type="submit" data-testid="add-petty-btn" className="w-full bg-primary text-[#111827] font-bold py-3 rounded-xl hover:bg-primary_hover transition-colors">Save Entry</button>
        </form>

        <div className="lg:col-span-2 bg-card_bg border border-border_soft rounded-2xl overflow-hidden">
          <table className="w-full text-left text-sm">
            <thead className="border-b border-border_soft/50"><tr>
              <th className="px-5 py-3 text-[10px] uppercase tracking-widest text-text_secondary font-bold">Date</th>
              <th className="px-5 py-3 text-[10px] uppercase tracking-widest text-text_secondary font-bold">Description</th>
              <th className="px-5 py-3 text-[10px] uppercase tracking-widest text-text_secondary font-bold">Category</th>
              <th className="px-5 py-3 text-[10px] uppercase tracking-widest text-text_secondary font-bold text-right">Amount</th>
              <th className="px-5 py-3"></th>
            </tr></thead>
            <tbody className="divide-y divide-border_soft/40">
              {data.entries.length === 0 && <tr><td colSpan="5" className="px-5 py-8 text-center text-text_secondary">No entries yet.</td></tr>}
              {data.entries.map((e) => (
                <tr key={e.id}>
                  <td className="px-5 py-3 text-text_secondary font-mono">{e.date}</td>
                  <td className="px-5 py-3 text-text_primary font-semibold flex items-center gap-2">{e.description}{e.receipt_file && <a href={`${BACKEND_URL}/api/files/${e.receipt_file}`} target="_blank" rel="noreferrer" className="text-gold" title="View receipt"><FileText size={13} /></a>}</td>
                  <td className="px-5 py-3 text-text_secondary">{e.category}</td>
                  <td className={`px-5 py-3 text-right font-bold ${e.direction === "IN" ? "text-accent_success" : "text-accent_danger"}`}>{e.direction === "IN" ? "+" : "−"}₹{(e.amount || 0).toLocaleString()}</td>
                  <td className="px-5 py-3 text-right">{canDelete && <button onClick={() => delEntry(e.id)} className="text-text_secondary hover:text-accent_danger"><Trash2 size={15} /></button>}</td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>
      </div>
    </div>
  );
}
